import { useMemo } from "react"

import { sanitizeData } from "@l2r-front/l2r-geodata"
import { useTranslation } from "@l2r-front/l2r-i18n"
import { Source, useMapDispatchContext } from "@l2r-front/l2r-map"
import { useNetworkReferential } from "@l2r-front/l2r-networks"

import { I18N_NAMESPACE } from "../../../../common/constants/i18n"
import { useSpeedingsStateContext } from "../../contexts/SpeedingsContext"
import { useSpeedings } from "../../hooks/queries/useSpeedingsLayer"
import { getGradeFromValue } from "../../utils/valueUtils"

export function RoadSpeedingsSource(props) {
    const {
        road,
        ...sourceProps
    } = props

    const { t } = useTranslation(I18N_NAMESPACE)
    const { setError: setMapError } = useMapDispatchContext()
    const { currentYear } = useSpeedingsStateContext()

    const { data: speedings, isLoading: isLoadingSpeedings } = useSpeedings({
        as_geojson: true,
        linear_location_road: road,
        year: currentYear,
    }, {
        enabled: !!road && !!currentYear,
        onError: () => setMapError(true),
        errorSnackbarMessage: t(I18N_NAMESPACE, "containers.speedingsSource.error"),
    })

    const { data: referential, isLoading: isLoadingReferential } = useNetworkReferential({
        as_geojson: true,
        linear_location_road: road,
    }, {
        enabled: !!road,
        onError: () => setMapError(true),
        errorSnackbarMessage: t(I18N_NAMESPACE, "containers.speedingsSource.error"),
    })

    // Need to copy id into properties because of that mapbox issue: 
    // link:https://github.com/mapbox/mapbox-gl-js/issues/2716
    const sanitizedData = useMemo(() => {
        if (isLoadingSpeedings) {
            return null
        }

        if ((!speedings?.features?.length) && (isLoadingReferential || !referential?.features?.length)) {
            return null
        }

        (referential?.features || []).forEach(feature => {
            const featureExists = speedings?.features.some(speedingsFeature =>
                speedingsFeature.linear_location.road === feature.linear_location[0].road
                && speedingsFeature.linear_location.start === feature.linear_location[0].start
                && speedingsFeature.linear_location.end === feature.linear_location[0].end)

            if (!featureExists) {
                speedings?.features.push(feature)
            }
        })

        const newData = sanitizeData(speedings ?? referential)
        const newFeatures = []
        if (newData) {
            newData.features.reduce((acc, feature) => {
                const positiveValue = getGradeFromValue(feature.properties.speedDirections?.["+"]?.exceed_5_pct)
                const negativeValue = getGradeFromValue(feature.properties.speedDirections?.["-"]?.exceed_5_pct)

                if (positiveValue) {
                    acc.push({
                        ...feature,
                        properties: {
                            exceed5Pct: positiveValue ?? -1,
                            speedLimit: feature.properties.speedDirections?.["+"]?.speed_limit,
                            side: "RIGHT",
                        },
                    })
                }

                if (negativeValue) {
                    acc.push({
                        ...feature,
                        properties: {
                            exceed5Pct: negativeValue ?? -1,
                            speedLimit: feature.properties.speedDirections?.["-"]?.speed_limit,
                            side: "LEFT",
                        },
                        geometry: {
                            ...feature.geometry,
                            coordinates: feature.geometry.coordinates.toReversed(),
                        },
                    })
                }

                if (!positiveValue && !negativeValue) {
                    acc.push(feature)
                }

                return acc
            }, newFeatures)
            newData.features = newFeatures
        }
        return newData
    }, [speedings, isLoadingSpeedings, referential, isLoadingReferential])

    return (
        <Source {...sourceProps} type="geojson" data={sanitizedData} tolerance={.001} />
    )
}

RoadSpeedingsSource.propTypes = Source.propTypes